import dayjs from 'dayjs';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { CONTEXT } from '@/config/consts/commons';

import { IS_BR } from '../utils/registrationConfig';
import { isCpfValid, isRutValid } from '../utils/identityDocument';
import { isAdultByCountry } from '../utils/birthDateValidation';

function buildRegistrationSchema(t) {
  return yup.object().shape({
    birth_date: yup
      .mixed()
      .required(t('registration.page.errors.birth_date_required'))
      .test(
        'valid-date',
        t('registration.page.errors.birth_date_invalid'),
        (value) => {
          if (value == null) return false;
          const parsed = dayjs(value);
          if (!parsed.isValid()) return false;
          return !parsed.isAfter(dayjs(), 'day');
        }
      )
      .test(
        'adult',
        t('registration.page.errors.birth_date_underage'),
        (value) => {
          if (value == null) return true;
          const parsed = dayjs(value);
          if (!parsed.isValid()) return true;
          return isAdultByCountry(parsed, CONTEXT);
        }
      ),
    rut: IS_BR
      ? yup.string().trim()
      : yup
          .string()
          .trim()
          .required(t('registration.page.errors.rut_required'))
          .test(
            'valid-rut',
            t('registration.page.errors.rut_invalid'),
            isRutValid
          ),
    cpf: IS_BR
      ? yup
          .string()
          .trim()
          .required(t('registration.page.errors.cpf_required'))
          .test(
            'valid-cpf',
            t('registration.page.errors.cpf_invalid'),
            isCpfValid
          )
      : yup.string().trim(),
    cnpj: yup.string().trim(),
    neighborhood: IS_BR
      ? yup
          .string()
          .trim()
          .required(t('registration.page.errors.neighborhood_required'))
      : yup.string().trim(),
    address: yup
      .string()
      .trim()
      .min(1, t('validation:min_length', { value: 1 }))
      .max(200, t('validation:max_length', { value: 200 }))
      .required(t('registration.page.errors.address_required')),
    vehicle_type: yup
      .string()
      .trim()
      .required(t('registration.page.errors.vehicle_type_required')),
  });
}

export function useRegistrationForm(t) {
  const schema = useMemo(() => buildRegistrationSchema(t), [t]);

  return useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      birth_date: null,
      rut: '',
      cpf: '',
      cnpj: '',
      neighborhood: '',
      address: '',
      vehicle_type: '',
    },
  });
}
